<template>
  <dn-skeleton
    v-if="loading"
    :show-media="!image_hide"
  />
  <dn-link
    v-else
    :href="teaser.canonicalSlug"
    :class="[
      'dn-teaser-card', teaser.teaserPriority?.toLowerCase(), teaser.mobileBPlusClass?.toLowerCase(),
      fontFlex, featureSource?.class?.toLowerCase(), teaser.theme?.toLowerCase(),
      teaser.imageHideClass?.toLowerCase(), teaser.badge?.toLowerCase(),
      teaser.imagePosition?.toLowerCase(), teaser.imageFormat?.toLowerCase(), teaser.mobileImagePosition.toLowerCase(),
    ]"
    :external="external"
    :data-id="id"
  >
    <dn-card
      ref="cardBox"
      v-track-event="trackBinding"
      :tag="tag"
      :title="teaser.title"
      :strapline="teaser.strapline"
      :lead="teaser.lead"
      :data-teaser_type="teaser.teaserType"
    >
      <template
        v-if="!image_hide && teaser.badge"
        #badge
      >
        <span>
          <dn-icon
            v-if="badgeIcon"
            :name="badgeIcon"
          />
          {{ t(teaser.badge) }}
        </span>
      </template>
      <template
        v-if="teaser.kicker"
        #kicker
      >
        <span v-if="teaser.isOpinion">
          <dn-icon
            name="quote"
          />
        </span>
        <span>
          {{ teaser.kicker }}
        </span>
      </template>
      <template
        v-if="!image_hide && teaser.leadAsset"
        #assets
      >
        <dn-image
          v-bind="teaser.leadAsset"
          :loading-mode="loadingMode"
          :fetch-priority="loadingMode === 'eager' && (priority === 'A' || priority === 'A+') ? 'high' : 'low'"
        />
      </template>
      <template
        v-if="teaser.meta || published_at"
        #meta
      >
        <span v-if="featureSource?.title">
          {{ featureSource?.title }}
        </span>
        <span v-if="teaser.meta">
          {{ teaser.meta }}
        </span>
        <span v-if="teaser.paid && subscriptionStatusFrontTeaser">
          {{ subscriptionStatusFrontTeaser }}
        </span>
        <dn-date-time
          v-if="published_at"
          :value="published_at"
          :format="useRuntimeConfig().public?.timeFormatLatestNews || 'DD.MM.YYYY'"
        />
      </template>
    </dn-card>
  </dn-link>
</template>

<script lang="ts" setup>
// @ts-ignore
import debounce from 'lodash.debounce';
import type { ArticlePublication } from '~/types/article';
const { t } = useI18n();
const props = defineProps({
  id: {
    type: String,
    required: true
  },
  lead_asset: {
    type: Object,
    required: false,
    default: () => {
    }
  },
  tag: {
    type: String,
    required: false,
    default: 'h3'
  },
  title: {
    type: String,
    required: false,
    default: null
  },
  meta: {
    type: String,
    required: false,
    default: null
  },
  strapline: {
    type: String,
    required: false,
    default: null
  },
  badge: {
    type: String,
    required: false,
    default: null
  },
  kicker: {
    type: String,
    required: false,
    default: null
  },
  lead: {
    type: String,
    required: false,
    default: null
  },
  hide_lead: {
    type: Boolean,
    required: false,
    default: false
  },
  priority: {
    type: String,
    required: false,
    default: null
  },
  theme: {
    type: String,
    required: false,
    default: null
  },
  theme_layout: {
    type: String,
    required: false,
    default: null
  },
  skin: {
    type: String,
    required: false,
    default: null
  },
  image_position: {
    type: String,
    required: false,
    default: 'auto'
  },
  features: {
    type: Object,
    required: false,
    default: () => {
    }
  },
  prefetch: {
    type: Boolean,
    required: false,
    default: false
  },
  assetDefaultRatio: {
    type: String,
    required: false,
    default: ''
  },
  variant: {
    type: String,
    required: false,
    default: ''
  },
  url: {
    type: String,
    required: false,
    default: ''
  },
  mobile_image_position: {
    type: String,
    required: false,
    default: 'auto'
  },
  image_hide: {
    type: Boolean,
    required: false,
    default: false
  },
  mobile_bplus: {
    type: Boolean,
    required: false,
    default: false
  },
  loadingMode: {
    type: String,
    required: false,
    default: 'lazy'
  },
  teaser_type: {
    type: String,
    required: false
  },
  access_level: {
    type: String,
    required: false,
    default: null
  },
  canonical_slug: {
    type: String,
    required: false,
    default: null
  },
  article_type: {
    type: String,
    required: false,
    default: null
  },
  published_at: {
    type: String,
    required: false,
    default: null
  },
  publication: {
    type: Object as PropType<ArticlePublication>,
    required: false,
    default: () => {}
  },
  /**
   * Optional 1-based index of this card in its parent list. When provided
   * together with `total`, the click event includes a pre-computed
   * element_position attribute (e.g. "3/12"), avoiding the legacy DOM-walking
   * fallback in useDataLayerEvent.constructEventInfo(). Pass these from the
   * parent v-for to make click tracking cheaper on dense lists.
   */
  position: {
    type: Number,
    required: false,
    default: 0
  },
  total: {
    type: Number,
    required: false,
    default: 0
  }
});

const subscriptionStatusFrontTeaser = useRuntimeConfig().public.subscriptionStatusFrontTeaser;
const provider = inject('teaser_provider');
const showOpinionKicker = inject('show_opinion_kicker', false);
const assetDefaultRatio = props.assetDefaultRatio || inject('teaser_asset_default_ratio', '16x9');
const prefetchInject = inject('prefetch', true);
const embedEditionContent = inject('embed_edition_content', false);
const stripTitleBr = inject('strip_title_br', false);
const zoneType = inject('zone_type', ZONE_TYPE_MANUAL);
const textFlexFontSize = ref(null);

const cardBox = ref(null);

const prefetch = computed(() => {
  return (props.prefetch || prefetchInject);
});

const shouldFetch = computed(() => !!(prefetch.value && provider && props.id));

const { query } = useTansQuery();

const { data, loading } = (() => {
  if (!provider || !shouldFetch.value) {
    return { data: ref({}), loading: ref(false) };
  }

  try {
    const providerConfig = useRuntimeConfig()?.public?.[provider];
    const api = import.meta.client && providerConfig?.api ? providerConfig?.api : generateServerUrl(useRuntimeConfig()?.[provider]?.originApi);

    if (!api) {
      return { data: ref({}), loading: ref(false) };
    }
    const { replaceUrl } = useUrlReplace();
    const apiUrl = computed(() => replaceUrl(api, embedEditionContent ? { id: props.id, embed_edition_content: 'true' } : { id: props.id }));
    const queryOptions = computed(() => ({ provider, id: props.id }));
    const authHeader = kongApiAuthHeader(useRuntimeConfig().dnmgAuthToken as string);

    return query({
      api: apiUrl,
      options: queryOptions,
      enabled: shouldFetch,
      transform: transformTeaser,
      headers: authHeader
    });
  } catch (e) {
    throw createError(`Error fetching teaser ${props.id} data for ${provider || 'undefined provider'} ${e}`);
  }
})();

const external = computed(() => {
  return !props.id.match(/^2-\d-\d+$/) || data.value?.type === 'featureArticle' || props.article_type === 'featureArticle';
});

const teaser = computed(() => {
  const editionContent = data.value?.editionContent;
  const rawTitle = props.title || editionContent?.title || data.value?.headFront || data.value?.title || ' ';
  const title = stripTitleBr ? rawTitle.replace(/<br\s*\/?>/gi, ' ') : rawTitle;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { layout, caption, credit, ...leadAsset } = props.lead_asset || editionContent?.leadAsset || data.value?.frontAsset || data.value?.leadAsset || {};
  leadAsset.alt = title;
  leadAsset.ratio = props.features?.['image-format'] && assetDefaultRatio
    ? (props.features?.['image-format'] === 'auto' || props.features?.['image-format'] === 'fill') ? assetDefaultRatio : props.features?.['image-format']
    : assetDefaultRatio || undefined;
  leadAsset.matchAreaOfInterest = props.features?.['image-crop-mode'] === 'areaOfInterest' || false;

  const getCanonicalSlug = (data, props) => {
    const url = data.value?.canonicalSlug || props.canonical_slug || props.url || `/${props.id}`;
    return props.variant
      ? `${url}${url.includes('?') ? '&' : '?'}abtest=${props.variant.toLowerCase()}`
      : url;
  };

  const getKicker = (data, props) => {
    if (props.kicker) {
      return props.kicker;
    }
    if (editionContent?.kicker) {
      return editionContent.kicker;
    }
    return showOpinionKicker && (props.theme === 'opinion' || data.value?.metadata?.theme === 'opinion') && data.value?.byline?.authors?.length === 1 ? data.value?.byline?.authors[0]?.name : '';
  };
  return {
    leadAsset,
    title,
    type: data.value?.type || '',
    canonicalSlug: getCanonicalSlug(data, props),
    lead: props.hide_lead ? null : props.lead || data.value?.intro,
    badge: props.features?.badge || editionContent?.features?.badge || null,
    meta: props.meta || editionContent?.meta || (useRuntimeConfig().public.showCategoryAsTopic ? data.value?.categories?.find(category => category.default)?.title : data.value?.topic?.label) || '',
    kicker: getKicker(data, props),
    strapline: props.strapline,
    paid: props.access_level === 'paywall' || data.value?.accessLevel === 'paywall',
    theme: props.theme_layout ? (props.theme === 'opinion' ? 'opinion-font' : '') : (props.theme || editionContent?.theme || data.value?.metadata?.theme),
    isOpinion: props.theme === 'opinion' || data.value?.metadata?.theme === 'opinion',
    teaserPriority: props.priority ? `dn-teaser-card-${props.priority}` : '',
    imageHideClass: (props.image_hide) ? 'dn-image-hide' : '',
    mobileBPlusClass: props.mobile_bplus ? 'dn-teaser-card-b+' : '',
    imagePosition: props.image_position !== 'auto' ? `dn-image-${props.image_position}` : (editionContent?.imagePosition ? `dn-image-${editionContent.imagePosition}` : 'dn-image-auto'),
    imageFormat: `dn-image-format-${props.features?.['image-format'] ? props.features?.['image-format'] : 'auto'}`,
    mobileImagePosition: `dn-image-mobile-${props.mobile_image_position !== 'auto' ? props.mobile_image_position : (editionContent?.mobileImagePosition || 'auto')}`,
    teaserType: props.teaser_type || '',
    publication: props.publication?.name || data.value?.publication?.name || ''
  };
});

const featureSource = computed(() => {
  const sourceName = props.features?.source || data.value?.editionContent?.features?.source;
  if (sourceName) {
    return useRuntimeConfig().public?.featurePublications?.[sourceName];
  }
  return null;
});

/*
 * Getter form so reactive teaser data is read at click time (per migration
 * doc). element_position is pre-computed from the position/total props when
 * the parent passes them, removing the legacy fallback's per-click DOM walk
 * for this card.
 */
const trackBinding = () => ({
  eventName: 'article clicked',
  component: { zone: 'frontpage', element: 'dn-card' },
  attributes: {
    ...(props.position && props.total ? { element_position: `${props.position}/${props.total}` } : {}),
    title: teaser.value.title,
    canonical_slug: teaser.value.canonicalSlug,
    type: teaser.value.type,
    teaser_type: teaser.value.teaserType,
    paid: teaser.value.paid,
    publication: teaser.value.publication,
    theme: teaser.value.theme,
    meta: teaser.value.meta,
    kicker: teaser.value.kicker,
    badge: teaser.value.badge,
    zone_type: zoneType
  },
  // contract-only — sourced from data/props, not added to the teaser view-model
  content: {
    id: props.id || data.value?.id || '',
    category: data.value?.categories?.find(category => category.default)?.title || data.value?.categories?.[0]?.title || '',
    topic: data.value?.topic?.name || ''
  }
});

const badgeIcon = computed(() => {
  const badge = props.features?.badge || data.value?.editionContent?.features?.badge;
  return (badge === 'breaking' || badge === 'live') ? 'pulsating' : badge === 'podcast' ? 'podcast' : '';
});

const fontFlex = computed(() => {
  return props.features?.['title-size'] === 'full' && !stripTitleBr ? 'dn-title-flex' : '';
});

const fitTextToParent = (minSize, maxSize) => {
  const parent = cardBox.value?.$el || cardBox.value;
  const title = parent?.querySelector('.title');
  const strongText = title?.querySelector('strong');
  const titleText = title?.innerHTML?.trim() || '';
  const titleStyles = window.getComputedStyle(title);
  const tempTextElement = document.createElement('span');
  const titleMaxWidth = title.offsetWidth - parseFloat(titleStyles.paddingLeft) - parseFloat(titleStyles.paddingRight);
  const titleFontFamily = titleStyles.fontFamily;
  const titleFontWeight = titleStyles.fontWeight;
  tempTextElement.style.position = 'absolute';
  tempTextElement.style.bottom = '0';
  tempTextElement.style.left = '0';
  tempTextElement.style.fontFamily = titleFontFamily;
  tempTextElement.style.fontWeight = titleFontWeight;
  tempTextElement.style.visibility = 'hidden';
  tempTextElement.style.whiteSpace = 'nowrap';
  tempTextElement.innerHTML = titleText;

  if (strongText) {
    const strongStyles = window.getComputedStyle(strongText);
    const strongFontFamily = strongStyles.fontFamily;
    const strongFontWeight = strongStyles.fontWeight;
    const childElements = tempTextElement.querySelectorAll('strong');
    childElements.forEach((childStrong) => {
      childStrong.style.fontFamily = strongFontFamily;
      childStrong.style.fontWeight = strongFontWeight;
    });
  }

  document.body.appendChild(tempTextElement);

  if (!title) {
    console.error('Title element not found!');
    return;
  }

  let low = minSize;
  let high = maxSize;
  let textFlexFontSize = minSize;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    tempTextElement.style.fontSize = `${mid}px`;

    if (tempTextElement.scrollWidth <= titleMaxWidth && mid > minSize) {
      textFlexFontSize = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  document.body.removeChild(tempTextElement);
  return `${textFlexFontSize}px`;
};

const handleResize = () => {
  return fitTextToParent(16, 200);
};

const debouncedHandleResize = debounce(handleResize, 500);
onMounted(() => {
  if (props.features?.['title-size'] === 'full' && !stripTitleBr) {
    textFlexFontSize.value = handleResize();
    useResizeObserver(cardBox, debouncedHandleResize);
  }
});
</script>

<style lang="scss">
.dn-teaser-card {
  --dn-link_display: var(--dn-teaser-card--link_display);
  --dn-link_color: var(--dn-teaser-card--link-color);
  --dn-link_text-decoration: var(--dn-teaser-card--link-text-decoration);
  --dn-link_color__hover: var(--dn-teaser-card--link-color__hover);
  --dn-link_text-decoration__hover: var(--dn-teaser-card--link-text-decoration__hover);
  --dn-link_color__active: var(--dn-teaser-card--link-color__active);
  --dn-link_text-decoration__active: var(--dn-teaser-card--link-text-decoration__active);
  --dn-card_background: var(--dn-teaser-card--card_background);
  --dn-card_align-content: var(--dn-teaser-card--card_align-content);
  --dn-card_border-radius: var(--dn-teaser-card--card_border-radius);
  --dn-card_border-bottom-width: var(--dn-teaser-card--card_border-bottom-width);
  --dn-card_border-bottom-color: var(--dn-teaser-card--card_border-bottom-color);
  --dn-card_border-right-width: var(--dn-teaser-card--card_border-right-width);
  --dn-card_border-right-color: var(--dn-teaser-card--card_border-right-color);
  --dn-card_padding: var(--dn-teaser-card--card_padding);
  --dn-card_font-family: var(--dn-teaser-card--card_font-family);
  --dn-card_row-gap: var(--dn-teaser-card--card_row-gap);
  --dn-card_column-gap: var(--dn-teaser-card--card_column-gap);
  --dn-card_grid-template: var(--dn-teaser-card--card_grid-template);
  --dn-card_assets_grid-area: var(--dn-teaser-card--card_assets_grid-area);
  --dn-card_assets_fill: var(--dn-teaser-card--card_assets_fill);
  --dn-card_assets_ratio: var(--dn-teaser-card--card_assets_ratio);
  --dn-card_assets_overlay: var(--dn-teaser-card--card_assets_overlay);
  --dn-card_image_border-radius: var(--dn-teaser-card--card_image_border-radius);
  --dn-card_image_height: var(--dn-teaser-card--card_image_height);
  --dn-card_image_picture_height: var(--dn-teaser-card--card_image_picture_height);
  --dn-card_kicker_display: var(--dn-teaser-card--card_kicker_display);
  --dn-card_kicker_grid-area: var(--dn-teaser-card--card_kicker_grid-area);
  --dn-card_badge_grid-area: var(--dn-teaser-card--card_badge_grid-area);
  --dn-card_badge_align-content: var(--dn-teaser-card--card_badge_align-content);
  --dn-card_strapline_grid-area: var(--dn-teaser-card--card_strapline_grid-area);
  --dn-card_title_grid-area: var(--dn-teaser-card--card_title_grid-area);
  --dn-card_lead_grid-area: var(--dn-teaser-card--card_lead_grid-area);
  --dn-card_meta_grid-area: var(--dn-teaser-card--card_meta_grid-area);
  --dn-card_pseudo_grid-area: var(--dn-teaser-card--card_pseudo_grid-area);
  --dn-card_pseudo_display: var(--dn-teaser-card--card_pseudo_display);
  --dn-card_pseudo_border-bottom-width: var(--dn-teaser-card--card_pseudo_border-bottom-width);
  --dn-card_pseudo_border-bottom-color: var(--dn-teaser-card--card_pseudo_border-bottom-color);
  --dn-card_meta_justify-content: var(--dn-teaser-card--card_meta_justify-content);
  --dn-card--badge_background: var(--dn-teaser-card--badge_background);
  --dn-card--badge_border-radius: var(--dn-teaser-card--badge_border-radius);
  --dn-card--badge_font-family: var(--dn-teaser-card--badge_font-family);
  --dn-card--badge_font-weight: var(--dn-teaser-card--badge_font-weight);
  --dn-card--badge_font-size: var(--dn-teaser-card--badge_font-size);
  --dn-card--badge_line-height: var(--dn-teaser-card--badge_line-height);
  --dn-card--badge_letter-spacing: var(--dn-teaser-card--badge_letter-spacing);
  --dn-card--badge_color: var(--dn-teaser-card--badge_color);
  --dn-card--badge_text-transform: var(--dn-teaser-card--badge_text-transform);
  --dn-card--badge_text-align: var(--dn-teaser-card--badge_text-align);
  --dn-card--badge_spacing: var(--dn-teaser-card--badge_spacing);
  --dn-card--badge_spacing-inner: var(--dn-teaser-card--badge_spacing-inner);
  --dn-card--assets_spacing: var(--dn-teaser-card--assets_spacing);
  --dn-card--kicker_font-family: var(--dn-teaser-card--kicker_font-family);
  --dn-card--kicker_font-weight: var(--dn-teaser-card--kicker_font-weight);
  --dn-card--kicker_font-size: var(--dn-teaser-card--kicker_font-size);
  --dn-card--kicker_line-height: var(--dn-teaser-card--kicker_line-height);
  --dn-card--kicker_letter-spacing: var(--dn-teaser-card--kicker_letter-spacing);
  --dn-card--kicker_color: var(--dn-teaser-card--kicker_color);
  --dn-card--kicker_text-transform: var(--dn-teaser-card--kicker_text-transform);
  --dn-card--kicker_text-align: var(--dn-teaser-card--kicker_text-align);
  --dn-card--kicker_spacing: var(--dn-teaser-card--kicker_spacing);
  --dn-card--kicker-icon_color: var(--dn-teaser-card--kicker-icon_color);
  --dn-card--strapline_font-weight: var(--dn-teaser-card--strapline_font-weight);
  --dn-card--strapline_font-size: var(--dn-teaser-card--strapline_font-size);
  --dn-card--strapline_line-height: var(--dn-teaser-card--strapline_line-height);
  --dn-card--strapline_letter-spacing: var(--dn-teaser-card--strapline_letter-spacing);
  --dn-card--strapline_color: var(--dn-teaser-card--strapline_color);
  --dn-card--title_strong_font-family: var(--dn-teaser-card--title_strong_font-family);
  --dn-card--title_strong_font-weight: var(--dn-teaser-card--title_strong_font-weight);
  --dn-card--title_regular_font-family: var(--dn-teaser-card--title_regular_font-family);
  --dn-card--title_regular_font-weight: var(--dn-teaser-card--title_regular_font-weight);
  --dn-card--title_line-height: var(--dn-teaser-card--title_line-height);
  --dn-card--title_letter-spacing: var(--dn-teaser-card--title_letter-spacing);
  --dn-card--title_color: var(--dn-teaser-card--title_color);
  --dn-card--title_text-align: var(--dn-teaser-card--title_text-align);
  --dn-card--title_text-transform: var(--dn-teaser-card--title_text-transform);
  --dn-card--lead_max-width: var(--dn-teaser-card--lead_max-width);
  --dn-card--lead_place-self: var(--dn-teaser-card--lead_place-self);
  --dn-card--lead_font-family: var(--dn-teaser-card--lead_font-family);
  --dn-card--lead_font-weight: var(--dn-teaser-card--lead_font-weight);
  --dn-card--lead_font-size: var(--dn-teaser-card--lead_font-size);
  --dn-card--lead_line-height: var(--dn-teaser-card--lead_line-height);
  --dn-card--lead_letter-spacing: var(--dn-teaser-card--lead_letter-spacing);
  --dn-card--lead_color: var(--dn-teaser-card--lead_color);
  --dn-card--lead_text-align: var(--dn-teaser-card--lead_text-align);
  --dn-card--lead_spacing: var(--dn-teaser-card--lead_spacing);
  --dn-card--meta_font-family: var(--dn-teaser-card--meta_font-family);
  --dn-card--meta_font-weight: var(--dn-teaser-card--meta_font-weight);
  --dn-card--meta_font-size: var(--dn-teaser-card--meta_font-size);
  --dn-card--meta_line-height: var(--dn-teaser-card--meta_line-height);
  --dn-card--meta_letter-spacing: var(--dn-teaser-card--meta_letter-spacing);
  --dn-card--meta_color: var(--dn-teaser-card--meta_color);
  --dn-card--meta_text-align: var(--dn-teaser-card--meta_text-align);
  --dn-card--meta_text-transform: var(--dn-teaser-card--meta_text-transform);
  --dn-card--meta_spacing: var(--dn-teaser-card--meta_spacing);
  --dn-card--date-time_font-size: var(--dn-teaser-card--date-time_font-size);
  --dn-card--date-time_font-weight: var(--dn-teaser-card--date-time_font-weight);
  --dn-card--date-time_color: var(--dn-teaser-card--date-time_color);
  --dn-card--date-time_line-height: var(--dn-teaser-card--date-time_line-height);

  display: grid;
  container-type: inline-size;

  .title {
    place-self: var(--dn-teaser-card--title_place-self, auto);

    --dn-card--title-flex_font-size: v-bind('textFlexFontSize');
    --dn-card--title_font-family: var(--dn-teaser-card--title_font-family);
    --dn-card--title_font-size: var(--dn-card--title-flex_font-size, var(--dn-teaser-card--title_font-size));
    --dn-card--title_font-weight: var(--dn-teaser-card--title_font-weight);
    --dn-card--title_spacing: var(--dn-teaser-card--title_spacing);
    --dn-card--title_max-width: var(--dn-teaser-card--title_max-width);
    --dn-card--title_width: var(--dn-teaser-card--title_width);
    --dn-card--title_white-space: var(--dn-teaser-card--title_white-space);
  }

  &:hover {
    --dn-link_color__hover: var(--dn-teaser-card--link-color__hover);

    opacity: 0.9;
  }

  &.highlighted {
    border-radius: var(--border-radius-teaser-max);
    outline: 4px solid #7d7df7;
  }
}
</style>
